import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Calendar,
  CalendarCheck,
  CalendarClock,
  CalendarRange,
  Check,
  Folder,
  FolderMinus,
  Search,
  Sun,
  X,
} from 'lucide-react';
import type { Task, TaskPriority } from '../../../lib/tasksApi';
import type { TaskStatusDef } from '../../../lib/taskStatusesApi';
import type { Department } from '../../../lib/departmentsApi';
import type { Person } from '../../../lib/peopleApi';
import { usePeople } from '../../../hooks/usePeople';
import { useDepartments } from '../../../hooks/useDepartments';
import { useProjects } from '../../../hooks/useProjects';
import { useTaskStatuses } from '../../../hooks/useTaskStatuses';
import { useGeneralProject, usePersonalProject } from '../../../hooks/useTasks';
import { useAuthStore } from '../../../store/useAuthStore';
import { useI18n } from '../../../contexts/I18nContext';
import { useToast } from './Toast';
import { useConfirm } from '../../ui/ConfirmProvider';
import { Popover } from './Popover';
import {
  PRIORITY_COLOR,
  PRIORITY_LABEL,
  PRIORITY_MENU_ORDER,
  PriorityIcon,
  StatusIcon,
} from './taskVisuals';
import { useOptimisticTaskUpdate } from './useOptimisticTaskUpdate';
import {
  DUE_BG,
  DUE_COLOR,
  dueUrgency,
  formatDayDate,
  formatDueLabel,
  quickDueOptions,
  toDateInputValue,
  toDueISO,
} from './dueDate';
import { Avatar } from './Avatar';
import { CalendarPanel } from '../../ui/DateField';

/**
 * CLE-174 — inline field editors for a task row. Each is a trigger button that
 * opens a `Popover` and applies the change optimistically via
 * `useOptimisticTaskUpdate`.
 */

// --- Status -----------------------------------------------------------------

interface EditorProps {
  task: Task;
  /** Notifies the row when the popover opens/closes so it can suppress click. */
  onOpenChange?: (open: boolean) => void;
}

export function StatusEditor({ task, onOpenChange }: EditorProps) {
  const update = useOptimisticTaskUpdate();
  const { listGroupOrder } = useTaskStatuses();

  function setStatus(status: TaskStatusDef, close: () => void) {
    close();
    if (status.id === task.statusId) return;
    update.mutate({
      id: task.id,
      input: { statusId: status.id },
      patch: (t) => ({
        ...t,
        status: status.slug,
        statusId: status.id,
        statusLabel: status.name,
        statusColor: status.color,
        statusCategory: status.category,
        statusOrder: status.order,
      }),
    });
  }

  return (
    <Popover
      width={210}
      onOpenChange={onOpenChange}
      trigger={({ toggle, ref }) => (
        <button
          ref={ref}
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            toggle();
          }}
          className="flex h-5 w-5 items-center justify-center rounded hover:bg-gray-200"
          title={`Status: ${task.statusLabel}`}
          aria-label="Schimbă statusul"
        >
          <StatusIcon category={task.statusCategory} color={task.statusColor} size={14} />
        </button>
      )}
    >
      {(close) => (
        <div className="py-1">
          {listGroupOrder.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setStatus(s, close)}
              className="ui-menu-item justify-between"
            >
              <span className="flex items-center gap-2">
                <StatusIcon category={s.category} color={s.color} size={14} />
                <span style={{ color: s.color }}>{s.name}</span>
              </span>
              {s.id === task.statusId && <Check size={14} className="text-gray-400" />}
            </button>
          ))}
        </div>
      )}
    </Popover>
  );
}

// --- Priority ---------------------------------------------------------------

export function PriorityEditor({ task, onOpenChange }: EditorProps) {
  const update = useOptimisticTaskUpdate();

  function setPriority(priority: TaskPriority, close: () => void) {
    close();
    if (priority === task.priority) return;
    update.mutate({
      id: task.id,
      input: { priority },
      patch: (t) => ({ ...t, priority }),
    });
  }

  return (
    <Popover
      width={200}
      onOpenChange={onOpenChange}
      trigger={({ toggle, ref }) => (
        <button
          ref={ref}
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            toggle();
          }}
          className="flex h-5 w-5 items-center justify-center rounded text-gray-500 transition-transform duration-150 hover:scale-110 hover:bg-gray-200"
          title={`Prioritate: ${PRIORITY_LABEL[task.priority]}`}
          aria-label="Schimbă prioritatea"
        >
          <PriorityIcon priority={task.priority} size={16} />
        </button>
      )}
    >
      {(close) => (
        <div className="py-1">
          {PRIORITY_MENU_ORDER.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setPriority(p, close)}
              className="ui-menu-item justify-between"
            >
              <span className="flex items-center gap-2 text-gray-500">
                <PriorityIcon priority={p} size={16} />
                <span style={{ color: p === 'NONE' ? undefined : PRIORITY_COLOR[p] }}>
                  {PRIORITY_LABEL[p]}
                </span>
              </span>
              {p === task.priority && <Check size={14} className="text-gray-400" />}
            </button>
          ))}
        </div>
      )}
    </Popover>
  );
}

// --- Department ---------------------------------------------------------------

export function DepartmentEditor({ task, onOpenChange }: EditorProps) {
  const update = useOptimisticTaskUpdate();
  const { data: departments } = useDepartments();

  function setDepartment(dept: Department | null, close: () => void) {
    close();
    if ((dept?.id ?? null) === task.departmentId) return;
    update.mutate({
      id: task.id,
      input: { departmentId: dept?.id ?? null },
      patch: (t) => ({
        ...t,
        departmentId: dept?.id ?? null,
        department: dept ? { id: dept.id, name: dept.name, color: dept.color } : null,
      }),
    });
  }

  return (
    <Popover
      width={220}
      onOpenChange={onOpenChange}
      trigger={({ toggle, ref }) => (
        <button
          ref={ref}
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            toggle();
          }}
          className="flex h-5 w-5 items-center justify-center rounded hover:bg-gray-200"
          title={`Departament: ${task.department?.name ?? 'Fără departament'}`}
          aria-label="Schimbă departamentul"
        >
          <span
            className="h-2.5 w-2.5 rounded-full"
            style={{
              backgroundColor: task.department?.color ?? 'transparent',
              boxShadow: task.department ? undefined : 'inset 0 0 0 1.5px #6b6b75',
            }}
          />
        </button>
      )}
    >
      {(close) => (
        <div className="flex flex-col">
          <div className="max-h-64 overflow-y-auto py-1">
            {(departments ?? []).map((d) => (
              <button
                key={d.id}
                type="button"
                onClick={() => setDepartment(d, close)}
                className="ui-menu-item justify-between"
              >
                <span className="flex items-center gap-2">
                  <span
                    className="h-2.5 w-2.5 shrink-0 rounded-full"
                    style={{ backgroundColor: d.color }}
                  />
                  <span className="truncate">{d.name}</span>
                </span>
                {d.id === task.departmentId && <Check size={14} className="text-gray-400" />}
              </button>
            ))}
            {(departments ?? []).length === 0 && (
              <p className="px-3 py-2 text-sm text-gray-400">Niciun departament definit.</p>
            )}
          </div>
          {task.departmentId && (
            <div className="border-t border-border py-1">
              <button
                type="button"
                onClick={() => setDepartment(null, close)}
                className="ui-menu-item text-gray-500"
              >
                <X size={14} /> Elimină departamentul
              </button>
            </div>
          )}
        </div>
      )}
    </Popover>
  );
}

// --- Project ----------------------------------------------------------------

/**
 * Move a task to another project.
 *
 * Until now the project was the one property a task could not be corrected
 * after the fact — and it is the easiest to get wrong, because at creation
 * time the picker may have offered nothing useful. The server accepts the move
 * and re-issues the task's identifier from the destination's sequence, so the
 * tag stops naming a project the task no longer lives in; links shared under
 * the old tag keep resolving.
 *
 * The identifier is deliberately NOT patched optimistically: the new number is
 * minted server-side inside the move's transaction, and guessing it here would
 * flash a wrong tag before the refetch corrected it.
 */
export function ProjectEditor({ task, onOpenChange }: EditorProps) {
  const update = useOptimisticTaskUpdate();

  return (
    <Popover
      width={260}
      onOpenChange={onOpenChange}
      trigger={({ toggle, ref }) => (
        <button
          ref={ref}
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            toggle();
          }}
          className="flex h-5 w-5 items-center justify-center rounded text-gray-400 hover:bg-gray-200 hover:text-gray-700"
          title={`Proiect: ${task.projectName ?? 'Fără proiect'}`}
          aria-label="Mută în alt proiect"
        >
          <Folder size={13} />
        </button>
      )}
    >
      {(close) => <ProjectMenu task={task} update={update} close={close} />}
    </Popover>
  );
}

function ProjectMenu({
  task,
  update,
  close,
}: {
  task: Task;
  update: ReturnType<typeof useOptimisticTaskUpdate>;
  close: () => void;
}) {
  const [query, setQuery] = useState('');
  const { data, isLoading } = useProjects({ limit: 200 });
  // "Fără proiect" (the general container) is hidden from the projects list,
  // so the mover pins it on top as a destination of its own.
  const { data: general } = useGeneralProject();

  const projects = useMemo(() => {
    const all = data?.projects ?? [];
    const q = query.trim().toLowerCase();
    if (!q) return all;
    return all.filter(
      (p) => p.name.toLowerCase().includes(q) || (p.code ?? '').toLowerCase().includes(q),
    );
  }, [data, query]);
  const generalMatches =
    !!general && (!query.trim() || general.name.toLowerCase().includes(query.trim().toLowerCase()));

  function move(projectId: string) {
    close();
    if (projectId === task.projectId) return;
    const target =
      (data?.projects ?? []).find((p) => p.id === projectId) ??
      (general && general.id === projectId ? general : undefined);
    update.mutate({
      id: task.id,
      input: { projectId },
      patch: (t) => ({
        ...t,
        projectId,
        projectName: target?.name ?? t.projectName,
      }),
    });
  }

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-2 border-b border-border px-2 py-1.5">
        <Search size={13} className="shrink-0 text-gray-400" />
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Caută proiect…"
          className="w-full bg-transparent text-[13px] text-gray-800 placeholder:text-gray-400 focus:outline-none"
        />
      </div>
      <div className="max-h-64 overflow-y-auto py-1">
        {generalMatches && (
          <button
            type="button"
            onClick={() => move(general.id)}
            className="ui-menu-item justify-between"
          >
            <span className="flex min-w-0 items-center gap-2">
              <FolderMinus size={13} className="shrink-0 text-gray-400" />
              <span className="truncate">{general.name}</span>
            </span>
            {general.id === task.projectId && (
              <Check size={14} className="shrink-0 text-gray-400" />
            )}
          </button>
        )}
        {projects.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => move(p.id)}
            className="ui-menu-item justify-between"
          >
            <span className="flex min-w-0 items-center gap-2">
              <Folder size={13} className="shrink-0 text-gray-400" />
              <span className="truncate">{p.name}</span>
            </span>
            {p.id === task.projectId && <Check size={14} className="shrink-0 text-gray-400" />}
          </button>
        ))}
        {projects.length === 0 && !generalMatches && (
          <p className="px-3 py-2 text-sm text-gray-400">
            {isLoading ? 'Se încarcă…' : 'Niciun proiect găsit.'}
          </p>
        )}
      </div>
    </div>
  );
}

// --- Assignee ---------------------------------------------------------------

export function AssigneeEditor({ task, onOpenChange }: EditorProps) {
  const update = useOptimisticTaskUpdate();

  return (
    <Popover
      width={260}
      align="end"
      onOpenChange={onOpenChange}
      trigger={({ toggle, ref }) => (
        <button
          ref={ref}
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            toggle();
          }}
          className="flex items-center rounded-full ring-brand-600 transition-shadow hover:ring-2"
          title={task.assignee ? task.assignee.name : 'Neatribuit'}
          aria-label="Schimbă responsabilul"
        >
          <Avatar name={task.assignee?.name ?? null} size={20} />
        </button>
      )}
    >
      {(close) => <AssigneeMenu task={task} update={update} close={close} />}
    </Popover>
  );
}

function AssigneeMenu({
  task,
  update,
  close,
}: {
  task: Task;
  update: ReturnType<typeof useOptimisticTaskUpdate>;
  close: () => void;
}) {
  const [query, setQuery] = useState('');
  const { data: people } = usePeople({ active: 'true' });
  const { data: departments } = useDepartments();

  // Personal-task transfer (admin only): reassigning a task in the caller's
  // own personal list SENDS it to the colleague's personal list wholesale.
  // Non-admins cannot reassign personal tasks at all, so their menu only
  // offers themselves (no option that would just 422).
  const callerId = useAuthStore((s) => s.user?.id ?? null);
  const isAdmin = useAuthStore((s) => s.user?.role === 'ADMIN');
  const { data: personalProject } = usePersonalProject();
  const isPersonalTask = !!personalProject && personalProject.id === task.projectId;
  const { t } = useI18n();
  const toast = useToast();
  const confirm = useConfirm();
  const qc = useQueryClient();

  // Only people linked to a User account can be set as a task assignee, since
  // the task assignee FK points at User, not Person.
  const members = useMemo(() => {
    let assignable = (people ?? []).filter((p) => p.userId);
    if (isPersonalTask && !isAdmin) {
      assignable = assignable.filter((p) => p.userId === callerId);
    }
    const q = query.trim().toLowerCase();
    return q ? assignable.filter((p) => p.fullName.toLowerCase().includes(q)) : assignable;
  }, [people, query, isPersonalTask, isAdmin, callerId]);

  async function assign(person: Person | null, close2: () => void) {
    close2();
    const userId = person?.userId ?? null;
    const name = person?.fullName ?? null;
    if (userId === task.assigneeId) return;

    if (isPersonalTask && isAdmin && userId && userId !== callerId) {
      // Transfer: the task moves into the colleague's personal project and
      // disappears from the sender's list — confirm before it vanishes.
      if (!(await confirm({ message: t('personalTasks.transfer.confirm', { name: name ?? '' }) })))
        return;
      update.mutate(
        {
          id: task.id,
          // No department adoption here — the backend forces departmentId null
          // and derives the destination project from the assignee.
          input: { assigneeId: userId },
          patch: (tk) => ({
            ...tk,
            assigneeId: userId,
            assignee: name ? { id: userId, name } : null,
          }),
        },
        {
          onSuccess: () => {
            toast.show(t('personalTasks.transfer.success', { name: name ?? '' }));
            // The row disappears via the list invalidation in onSettled; an
            // open detail view refetches into the existing 404 screen.
            void qc.invalidateQueries({ queryKey: ['task', task.id] });
          },
          onError: () => toast.show(t('personalTasks.transfer.failed')),
        },
      );
      return;
    }

    // CLE-DASH: a task without a department adopts the new assignee's — same
    // defaulting the backend applies on create.
    const dept =
      userId && task.departmentId == null && person?.departmentId
        ? (departments?.find((d) => d.id === person.departmentId) ?? null)
        : null;
    update.mutate({
      id: task.id,
      input: { assigneeId: userId, ...(dept ? { departmentId: dept.id } : {}) },
      patch: (t) => ({
        ...t,
        assigneeId: userId,
        assignee: userId && name ? { id: userId, name } : null,
        ...(dept
          ? {
              departmentId: dept.id,
              department: { id: dept.id, name: dept.name, color: dept.color },
            }
          : {}),
      }),
    });
  }

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-2 border-b border-border px-3 py-2">
        <Search size={14} className="text-gray-400" />
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={
            isPersonalTask && isAdmin ? t('personalTasks.transfer.menuHeader') : 'Caută membri…'
          }
          className="w-full bg-transparent text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none"
        />
      </div>
      <div className="max-h-64 overflow-y-auto py-1">
        {members.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => void assign(p, close)}
            className="ui-menu-item justify-between"
          >
            <span className="flex items-center gap-2">
              <Avatar name={p.fullName} size={20} />
              <span className="truncate">{p.fullName}</span>
            </span>
            {p.userId === task.assigneeId && <Check size={14} className="text-gray-400" />}
          </button>
        ))}
        {members.length === 0 && (
          <p className="px-3 py-2 text-sm text-gray-400">Niciun membru găsit.</p>
        )}
      </div>
      {/* Personal tasks are always assigned to their owner — no unassign. */}
      {!isPersonalTask && (
        <div className="border-t border-border py-1">
          <button
            type="button"
            onClick={() => void assign(null, close)}
            className="ui-menu-item text-gray-500"
          >
            <X size={14} /> Elimină responsabil
          </button>
        </div>
      )}
    </div>
  );
}

// --- Due date ---------------------------------------------------------------

export function DueDateEditor({
  task,
  onOpenChange,
  alwaysVisible = false,
}: EditorProps & { alwaysVisible?: boolean }) {
  const update = useOptimisticTaskUpdate();
  const urgency = dueUrgency(task.dueDate);
  const label = formatDueLabel(task.dueDate);

  function setDue(iso: string | null, close: () => void) {
    close();
    update.mutate({
      id: task.id,
      input: { dueDate: iso },
      patch: (t) => ({ ...t, dueDate: iso }),
    });
  }

  return (
    <Popover
      width={280}
      align="end"
      onOpenChange={onOpenChange}
      trigger={({ toggle, ref }) =>
        task.dueDate ? (
          <button
            ref={ref}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              toggle();
            }}
            className="group/due inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[12px] font-medium hover:underline"
            style={{
              color: urgency !== 'none' ? DUE_COLOR[urgency] : undefined,
              backgroundColor: DUE_BG[urgency] ?? undefined,
            }}
            aria-label="Schimbă termenul"
          >
            <Calendar size={12} />
            {label}
          </button>
        ) : (
          <button
            ref={ref}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              toggle();
            }}
            className={`inline-flex h-5 w-5 items-center justify-center rounded text-gray-500 hover:bg-gray-200 ${
              alwaysVisible ? 'opacity-100' : 'opacity-0 group-hover/row:opacity-100'
            }`}
            aria-label="Setează termenul"
            title="Setează termenul"
          >
            <Calendar size={13} />
          </button>
        )
      }
    >
      {(close) => <DueDateMenu task={task} onPick={setDue} close={close} />}
    </Popover>
  );
}

function DueDateMenu({
  task,
  onPick,
  close,
}: {
  task: Task;
  onPick: (iso: string | null, close: () => void) => void;
  close: () => void;
}) {
  const [showCustom, setShowCustom] = useState(false);
  const options = useMemo(() => quickDueOptions(), []);

  return (
    <div className="flex flex-col">
      <div className="py-1">
        {task.dueDate && (
          <button
            type="button"
            onClick={() => onPick(null, close)}
            className="ui-menu-item justify-between text-gray-500"
          >
            <span className="flex items-center gap-2">
              <X size={14} /> Elimină termenul
            </span>
            <span className="text-xs text-gray-400">{formatDayDate(new Date(task.dueDate))}</span>
          </button>
        )}

        {!showCustom ? (
          <button
            type="button"
            onClick={() => setShowCustom(true)}
            className="ui-menu-item text-gray-700"
          >
            <CalendarClock size={14} /> Personalizat…
          </button>
        ) : (
          // Inlined rather than a nested `DateField`: this menu already lives in
          // a Popover portal, and a second one would read as an outside click.
          <CalendarPanel
            value={task.dueDate ? toDateInputValue(new Date(task.dueDate)) : ''}
            onPick={(key) => {
              const [y, m, d] = key.split('-').map(Number);
              onPick(toDueISO(new Date(y, m - 1, d)), close);
            }}
          />
        )}

        {options.map((o) => (
          <button
            key={o.key}
            type="button"
            onClick={() => onPick(toDueISO(o.date), close)}
            className="ui-menu-item justify-between"
          >
            <span className="flex items-center gap-2">
              {o.key === 'today' ? (
                <CalendarCheck size={14} />
              ) : o.key === 'tomorrow' ? (
                <Sun size={14} />
              ) : o.key === 'nextMonday' ? (
                <CalendarRange size={14} />
              ) : (
                <Calendar size={14} />
              )}
              {o.label}
            </span>
            <span className="text-xs text-gray-400">{formatDayDate(o.date)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
